import type { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { inject,Injectable } from '@angular/core';
import type { Observable } from 'rxjs';

import type {
  AvailableIncidentFiltersResponseDto,
  IncidentsControllerV2FindAll200,
  IncidentsControllerV2FindAllParams,
  IncidentsControllerV2GetAvailableFiltersParams,
  IncidentsControllerV2GetStatisticsParams,
  IncidentStatisticsResponseDto,
} from '../models';

type QueryParamValue = string | number | boolean | (string | number | boolean)[];

interface HttpRequestOptions {
  readonly context?: HttpContext;
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly params?: HttpParams | Record<string, QueryParamValue>;
}

function filterParams(params: Record<string, unknown>): Record<string, QueryParamValue> {
  const filtered: Record<string, QueryParamValue> = {};

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      const items = value.filter(
        (item): item is string | number | boolean => item != null && ['string', 'number', 'boolean'].includes(typeof item),
      );

      if (items.length) {
        filtered[key] = items;
      }
    } else if (value != null && ['string', 'number', 'boolean'].includes(typeof value)) {
      filtered[key] = value as QueryParamValue;
    }
  }

  return filtered;
}

@Injectable({ providedIn: 'root' })
export class IncidentsV2Service {
  private readonly http = inject(HttpClient);

  /** Returns a paginated, filtered, sorted list of incidents for the incidents grid. */
  incidentsControllerV2FindAll(
    clientId: string,
    params?: IncidentsControllerV2FindAllParams,
    options?: HttpRequestOptions,
  ): Observable<IncidentsControllerV2FindAll200> {
    return this.http.get<IncidentsControllerV2FindAll200>(`incidents/v2/clients/${encodeURIComponent(clientId)}/incidents`, {
      ...options,
      params: filterParams({ ...params, ...options?.params }),
    });
  }

  /** Returns aggregated statistics for incidents based on the provided filters. */
  incidentsControllerV2GetStatistics(
    clientId: string,
    params?: IncidentsControllerV2GetStatisticsParams,
    options?: HttpRequestOptions,
  ): Observable<IncidentStatisticsResponseDto> {
    return this.http.get<IncidentStatisticsResponseDto>(`incidents/v2/clients/${encodeURIComponent(clientId)}/incidents/statistics`, {
      ...options,
      params: filterParams({ ...params, ...options?.params }),
    });
  }

  /** Distinct severity/deviceType/building/floor/room values for open incidents, without fetching full rows. */
  incidentsControllerV2GetAvailableFilters(
    clientId: string,
    params?: IncidentsControllerV2GetAvailableFiltersParams,
    options?: HttpRequestOptions,
  ): Observable<AvailableIncidentFiltersResponseDto> {
    return this.http.get<AvailableIncidentFiltersResponseDto>(`incidents/v2/clients/${encodeURIComponent(clientId)}/incidents/filters`, {
      ...options,
      params: filterParams({ ...params, ...options?.params }),
    });
  }
}
